
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useUI } from "@/context/UIContext";
import { Navbar } from "@/components/Navbar";

export default function SetupPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { setIsOnboardingComplete } = useUI();

  // Extract the token and email from URL
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const type = searchParams.get("type");

  // Verify the user's email on component mount
  useEffect(() => {
    const verifyEmail = async () => {
      if (!token || !email) {
        toast({
          title: "Missing parameters",
          description: "The verification link appears to be invalid.",
          variant: "destructive"
        });
        setVerifying(false);
        return;
      }

      if (type === "recovery") {
        // This is a password reset, not verification
        setVerifying(false);
        return;
      }

      try {
        // Check if this is a signup verification or password reset
        const { error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: type === "recovery" ? "recovery" : "signup",
          email,
        });

        if (error) throw error;

        toast({
          title: "Email verified",
          description: "Your email has been verified. Please set your password to continue.",
        });
      } catch (error: any) {
        console.error("Verification error:", error);
        toast({
          title: "Verification failed",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token, email, type, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive"
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({ 
        password: password 
      });

      if (error) throw error;

      // Mark onboarding as complete
      setIsOnboardingComplete(true);

      toast({
        title: "Password set successfully",
        description: "You can now sign in with your email and password.",
      });

      // Try to sign in automatically
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email || "",
        password: password,
      });

      if (signInError) {
        // If auto sign-in fails, navigate to login page
        navigate("/auth");
      } else {
        // If successful, navigate to dashboard
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: "Error setting password",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Verifying your email...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4">
        <div className="container max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>{type === "recovery" ? "Reset Your Password" : "Set Your Password"}</CardTitle>
              <CardDescription>
                {type === "recovery" 
                  ? "Create a new secure password for your account" 
                  : "Complete your registration by setting a password"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Setting password..." : type === "recovery" ? "Reset Password" : "Complete Registration"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
